import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useProjectStore } from "../store/useProjectStore";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

const FEEDBACK_TYPES = [
  { value: "bug", label: "Bug" },
  { value: "idea", label: "Idea" },
  { value: "ai_quality", label: "AI quality" },
  { value: "ux_confusion", label: "UX confusion" },
  { value: "data_issue", label: "Data issue" },
  { value: "other", label: "Other" },
];

const FEEDBACK_SEVERITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

function buildContextPreview(route, project, initiative) {
  const projectName = project?.meta?.projectName || project?.projectName || "None";
  const initiativeName = initiative?.name || "None";

  return [
    { label: "Route", value: route || "/" },
    { label: "Project", value: project ? projectName : "None" },
    { label: "Initiative", value: initiative ? initiativeName : "None" },
  ];
}

export default function FeedbackWidget({ hideLauncher = false }) {
  const location = useLocation();
  const submitFeedback = useProjectStore((s) => s.submitFeedback);
  const activeRefs = useProjectStore((s) => s.selectActiveRefs());

  const [open, setOpen] = useState(false);
  const [type, setType] = useState("idea");
  const [severity, setSeverity] = useState("medium");
  const [message, setMessage] = useState("");
  const defaultMessagePlaceholder =
    "Tell us what happened, or what you would like to see.";
  const [messagePlaceholder, setMessagePlaceholder] = useState(defaultMessagePlaceholder);
  const [toast, setToast] = useState("");

  const route = location?.pathname || "/";
  const contextPreview = useMemo(
    () => buildContextPreview(route, activeRefs?.project, activeRefs?.initiative),
    [route, activeRefs?.project, activeRefs?.initiative]
  );

  const severityRequired = type === "bug";
  const canSubmit = message.trim().length > 0 && (!severityRequired || severity);

  const handleSubmit = () => {
    if (!canSubmit) return;

    submitFeedback?.({
      type,
      severity: severityRequired ? severity : null,
      message: message.trim(),
      context: {
        route,
        activeProjectId: activeRefs?.activeProjectId || null,
        activeInitiativeId: activeRefs?.activeInitiativeId || null,
      },
    });

    setOpen(false);
    setMessage("");
    setType("idea");
    setSeverity("medium");
    setToast("Feedback sent. Thank you.");
    window.setTimeout(() => setToast(""), 2600);
  };

  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setMessage("");
      setType("idea");
      setSeverity("medium");
      setMessagePlaceholder(defaultMessagePlaceholder);
    }
  };

  useEffect(() => {
    const handleOpen = (event) => {
      setOpen(true);
      const detail = event?.detail || {};
      if (detail.type) setType(detail.type);
      if (typeof detail.message === "string") {
        setMessage(detail.message);
      } else {
        setMessage("");
      }
      if (typeof detail.messagePlaceholder === "string") {
        setMessagePlaceholder(detail.messagePlaceholder);
      } else {
        setMessagePlaceholder(defaultMessagePlaceholder);
      }
    };
    window.addEventListener("actara:open-feedback", handleOpen);
    return () => window.removeEventListener("actara:open-feedback", handleOpen);
  }, []);

  return (
    <>
      {hideLauncher ? null : (
        <div className="fixed bottom-6 right-6 z-40">
          <Button type="button" onClick={() => setOpen(true)}>
            Feedback
          </Button>
        </div>
      )}

      {toast ? (
        <div className="fixed bottom-20 right-6 z-40 rounded-xl bg-slate-900 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      ) : null}

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Send feedback</DialogTitle>
            <DialogDescription>
              Share what you noticed. We will use this to improve the product.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger label="Type" placeholder="Select feedback type" />
              <SelectContent>
                {FEEDBACK_TYPES.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {severityRequired ? (
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger label="Severity" placeholder="Select severity" />
                <SelectContent>
                  {FEEDBACK_SEVERITIES.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}

            <Textarea
              label="Message"
              placeholder={messagePlaceholder}
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />

            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Context
              </div>
              <div className="mt-2 space-y-1 text-sm text-slate-700">
                {contextPreview.map((item) => (
                  <div key={item.label} className="flex items-start gap-2">
                    <span className="w-24 text-xs font-medium text-slate-500">{item.label}</span>
                    <span className="flex-1">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="button" disabled={!canSubmit} onClick={handleSubmit}>
                Submit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
